import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import useDebounce from '@/client/hooks/useDebounce';
import { getAsset } from '@/client/utils/assets';
import {
  CheckCircle2,
  XCircle,
  PauseCircle,
  Flag,
  Loader2,
  Filter,
  ChevronDown,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import CategoryFilterDrawer from '@/client/components/admin/ListingManagement/CategoryFilterDrawer';
import { useClientStore } from '@/client/stores/client';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useReviewQueueQuery,
  useReportedEntitiesQuery,
  ReviewQueueItem,
  ReportedEntity,
} from '@/client/api/listing-management/entity.query';
import {
  useTransitionEntityMutation,
  EntityStatus,
} from '@/client/api/listing-management/entity.mutation';

const STATUS_STYLES: Record<string, string> = {
  LIVE: 'bg-green-100 text-green-800',
  REVIEW: 'bg-amber-100 text-amber-800',
  REJECTED: 'bg-rose-100 text-rose-800',
  SUSPENDED: 'bg-orange-100 text-orange-800',
  DRAFT: 'bg-gray-100 text-gray-700',
};

const StatusBadge = ({ status }: { status: string | null }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
      STATUS_STYLES[status ?? ''] ?? 'bg-gray-100 text-gray-600'
    }`}
  >
    {status ?? 'UNKNOWN'}
  </span>
);

// A moderation action rendered as an icon button with a hover tooltip.
const ActionIconButton = ({
  label,
  onClick,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className={`rounded-md p-1.5 ${className ?? ''}`}
      >
        {children}
      </button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

/** Resolve an etid to its category label from the shared client-store tree. */
const findCatLabel = (cats: any[] | null | undefined, etid: number | null): string | null => {
  if (!etid || !cats) return null;
  for (const c of cats) {
    if (Number(c.etid) === etid) return c.name_friendly || c.name_menu || c.name_system || `#${etid}`;
    const found = findCatLabel(c.children, etid);
    if (found) return found;
  }
  return null;
};

// Shared confirm-and-transition machinery.
type PendingAction = {
  eid: number;
  name: string;
  status: EntityStatus;
  label: string;
} | null;

// ─── Review queue ────────────────────────────────────────────────────────────

const REVIEW_STATUSES = ['REVIEW', 'REJECTED', 'SUSPENDED', 'DRAFT'] as const;

export const ReviewQueuePanel: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('REVIEW');
  const [search, setSearch] = useState('');
  const [etid, setEtid] = useState('');
  const [typeDrawerOpen, setTypeDrawerOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState<PendingAction>(null);

  // Debounce so the query only fires ~400ms after typing stops.
  const debouncedSearch = useDebounce(search.trim(), 400);

  // A new search term or category resets to the first page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, etid]);

  const categories = useClientStore((s) => s.categories);
  const fetchCategories = useClientStore((s) => s.fetchCategories);
  useEffect(() => {
    if (etid && !categories) fetchCategories();
  }, [etid, categories, fetchCategories]);
  const typeLabel = useMemo(
    () => (etid ? findCatLabel(categories, Number(etid)) ?? `Category ${etid}` : 'All Types'),
    [categories, etid],
  );

  const { data, isLoading, isError, isFetching } = useReviewQueueQuery({
    status,
    search: debouncedSearch || undefined,
    ...(etid ? { etid: Number(etid) } : {}),
    page,
    page_size: 25,
  });
  const transition = useTransitionEntityMutation();

  const items = data?.queue ?? [];
  const pagination = data?.pagination;

  const act = (item: ReviewQueueItem, next: EntityStatus, label: string) =>
    setPending({ eid: item.eid, name: item.name ?? `#${item.eid}`, status: next, label });

  const confirm = async () => {
    if (!pending) return;
    try {
      await transition.mutateAsync({ eid: pending.eid, status: pending.status });
      toast.success(`${pending.name} → ${pending.status}`);
      setPending(null);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update the listing.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1">
          {REVIEW_STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => {
                setStatus(s);
                setPage(1);
              }}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                status === s ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <Input
          className="max-w-xs"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Category filter */}
        <div
          className={`flex h-9 items-center gap-2 rounded-md border px-3 text-sm ${
            etid ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-input bg-background text-gray-600'
          }`}
        >
          <button
            type="button"
            onClick={() => setTypeDrawerOpen(true)}
            className="flex items-center gap-2"
            title={typeLabel}
          >
            <Filter className="h-4 w-4 text-gray-400" />
            <span className="max-w-[10rem] truncate">{typeLabel}</span>
          </button>
          {etid ? (
            <button
              type="button"
              onClick={() => setEtid('')}
              className="rounded p-0.5 text-blue-500 hover:bg-blue-100"
              aria-label="Clear category"
            >
              <X className="h-4 w-4" />
            </button>
          ) : (
            <ChevronDown className="h-4 w-4 cursor-pointer text-gray-400" onClick={() => setTypeDrawerOpen(true)} />
          )}
        </div>

        {isFetching && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      </div>

      <TooltipProvider delayDuration={200}>
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div>
          <table className="w-full table-fixed divide-y divide-gray-200">
            <colgroup>
              <col className="w-[30%]" />
              <col className="w-[13%]" />
              <col className="w-[15%]" />
              <col className="w-[11%]" />
              <col className="w-[12%]" />
              <col className="w-[19%]" />
            </colgroup>
            <thead className="bg-gray-50">
              <tr>
                {['Listing', 'Type', 'Owner', 'Status', 'Submitted', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className={`px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${
                      h === 'Actions' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <td key={j} className="px-4 py-4">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))}

              {!isLoading && isError && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-red-600">
                    Could not load the review queue.
                  </td>
                </tr>
              )}

              {!isLoading && !isError && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">
                    Nothing in the {status.toLowerCase()} queue. 🎉
                  </td>
                </tr>
              )}

              {!isLoading &&
                items.map((item: ReviewQueueItem) => (
                  <tr
                    key={item.eid}
                    onClick={() => navigate(`/admin/listings/${item.eid}`)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={getAsset(item.cover as any)}
                          alt=""
                          loading="lazy"
                          className="h-11 w-11 shrink-0 rounded-lg border border-gray-100 bg-gray-50 object-cover"
                        />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-gray-900">
                            {item.name ?? `Listing #${item.eid}`}
                          </div>
                          {(item as any).ffid && (
                            <div className="truncate font-mono text-xs text-gray-400">{(item as any).ffid}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="truncate px-4 py-4 text-sm text-gray-600">
                      {item.type_name ?? '—'}
                    </td>
                    <td className="truncate px-4 py-4 text-sm text-gray-600">
                      {item.owner_name ?? (item.uid ? `uid ${item.uid}` : '—')}
                    </td>
                    <td className="px-4 py-4">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-xs text-gray-500">
                      {item.date_added
                        ? new Date(item.date_added).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <ActionIconButton
                          label="Approve (set live)"
                          onClick={() => act(item, 'LIVE', 'approve')}
                          className="text-green-600 hover:bg-green-50"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </ActionIconButton>
                        <ActionIconButton
                          label="Reject"
                          onClick={() => act(item, 'REJECTED', 'reject')}
                          className="text-rose-600 hover:bg-rose-50"
                        >
                          <XCircle className="h-4 w-4" />
                        </ActionIconButton>
                        <ActionIconButton
                          label="Suspend"
                          onClick={() => act(item, 'SUSPENDED', 'suspend')}
                          className="text-orange-600 hover:bg-orange-50"
                        >
                          <PauseCircle className="h-4 w-4" />
                        </ActionIconButton>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {!isLoading && !isError && pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3">
            <p className="text-xs text-gray-500">
              Page {pagination.page} of {pagination.total_pages} · {pagination.total} total
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pagination.total_pages || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
      </TooltipProvider>

      <CategoryFilterDrawer
        open={typeDrawerOpen}
        onClose={() => setTypeDrawerOpen(false)}
        value={etid}
        onChange={setEtid}
      />

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending ? `${pending.label[0].toUpperCase()}${pending.label.slice(1)} “${pending.name}”?` : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This moves the listing to <strong>{pending?.status}</strong>. Owners are
              notified of moderation changes. You can transition it again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setPending(null)} disabled={transition.isPending}>
              Cancel
            </Button>
            <Button
              variant={pending?.status === 'LIVE' ? 'default' : 'destructive'}
              onClick={confirm}
              disabled={transition.isPending}
            >
              {transition.isPending ? 'Working…' : `Confirm ${pending?.label ?? ''}`}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ─── Reported queue ──────────────────────────────────────────────────────────

export const ReportedQueuePanel: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState<PendingAction>(null);

  const { data, isLoading, isError, isFetching } = useReportedEntitiesQuery({
    page,
    page_size: 25,
  });
  const transition = useTransitionEntityMutation();

  const items = data?.reported ?? [];
  const totalPages = data?.total_pages ?? 0;

  const confirm = async () => {
    if (!pending) return;
    try {
      await transition.mutateAsync({ eid: pending.eid, status: pending.status });
      toast.success(`${pending.name} → ${pending.status}`);
      setPending(null);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update the listing.');
    }
  };

  return (
    <div className="space-y-4">
      <TooltipProvider delayDuration={200}>
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div>
          <table className="w-full table-fixed divide-y divide-gray-200">
            <colgroup>
              <col className="w-[46%]" />
              <col className="w-[16%]" />
              <col className="w-[14%]" />
              <col className="w-[24%]" />
            </colgroup>
            <thead className="bg-gray-50">
              <tr>
                {['Listing', 'Reports', 'Status', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className={`px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${
                      h === 'Actions' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 4 }).map((__, j) => (
                      <td key={j} className="px-4 py-4">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))}

              {!isLoading && isError && (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-sm text-red-600">
                    Could not load reported listings.
                  </td>
                </tr>
              )}

              {!isLoading && !isError && items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500">
                    No open reports. 🎉
                  </td>
                </tr>
              )}

              {!isLoading &&
                items.map((item: ReportedEntity) => (
                  <tr
                    key={item.eid}
                    onClick={() => navigate(`/admin/listings/${item.eid}`)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-4 py-4">
                      <div className="truncate text-sm font-medium text-gray-900">
                        {item.name ?? `Listing #${item.eid}`}
                      </div>
                      {(item as any).ffid && (
                        <div className="truncate font-mono text-xs text-gray-400">{(item as any).ffid}</div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-700">
                        <Flag className="h-3 w-3" />
                        {item.report_count}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <ActionIconButton
                          label="Suspend"
                          onClick={() =>
                            setPending({ eid: item.eid, name: item.name ?? `#${item.eid}`, status: 'SUSPENDED', label: 'suspend' })
                          }
                          className="text-orange-600 hover:bg-orange-50"
                        >
                          <PauseCircle className="h-4 w-4" />
                        </ActionIconButton>
                        <ActionIconButton
                          label="Reject"
                          onClick={() =>
                            setPending({ eid: item.eid, name: item.name ?? `#${item.eid}`, status: 'REJECTED', label: 'reject' })
                          }
                          className="text-rose-600 hover:bg-rose-50"
                        >
                          <XCircle className="h-4 w-4" />
                        </ActionIconButton>
                        <ActionIconButton
                          label="Dismiss reports (keep live)"
                          onClick={() =>
                            setPending({ eid: item.eid, name: item.name ?? `#${item.eid}`, status: 'LIVE', label: 'dismiss (keep live)' })
                          }
                          className="text-green-600 hover:bg-green-50"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </ActionIconButton>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {!isLoading && !isError && totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3">
            <p className="text-xs text-gray-500">
              Page {page} of {totalPages} · {data?.total ?? 0} reported
              {isFetching && <span className="ml-2 text-gray-400">syncing…</span>}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
      </TooltipProvider>

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending ? `Confirm: ${pending.label}` : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>
              “{pending?.name}” will move to <strong>{pending?.status}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setPending(null)} disabled={transition.isPending}>
              Cancel
            </Button>
            <Button
              variant={pending?.status === 'LIVE' ? 'default' : 'destructive'}
              onClick={confirm}
              disabled={transition.isPending}
            >
              {transition.isPending ? 'Working…' : 'Confirm'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
